import type { Account, FinanceCore, Meta, NetWorthData, ProjectionData, Rollups, Txn } from "./types";

/**
 * Finance shard model — the core of incremental sync (docs/
 * incremental-sync-strategy.md). The monolithic `core` / `transactions`
 * payloads stop being wire formats; instead the store materializes bundles
 * whose granularity matches how the data actually changes:
 *
 *   accounts-meta      account records without balance history (small, every import)
 *   meta               freshness/counts doc (tiny, every import)
 *   rollups            monthly+yearly statements (small, every import)
 *   projection         projection model (tiny, rare)
 *   balances:<year>    flattened balance snapshots — frozen for past years
 *   networth:<year>    that year's slice of the net-worth series — same
 *   txn:<year>         a frozen year's transactions
 *   txn:<YYYY-MM>      one hot-year month's transactions
 *
 * Frozen shards never change bytes, so their manifest hashes never change,
 * so a refresh never re-downloads them. The hot shards ARE the change and
 * they're small. This module is pure and isomorphic: the server materializes
 * with it, the client reassembles with it, and the equivalence test drives
 * both directions over the real fixture.
 */

export const SHARD_SCHEMA = 2;

/** Stored/served manifest: which bundles exist and a version hash for each.
 * The API response adds the server's app `build` hash at request time. */
export interface ShardManifest {
  schema: number;
  generatedAt: string;
  bundles: Record<string, string>;
}

export interface ShardManifestResponse extends ShardManifest {
  build: string;
}

export type BalanceRowTuple = [accountId: string, date: string, value: number];

export interface AccountsMetaBundle {
  accounts: Omit<Account, "balances">[];
}

export interface BalancesBundle {
  rows: BalanceRowTuple[];
}

export interface NetworthBundle {
  months: string[];
  series: Record<string, Record<string, number>>;
}

/** Split the monolithic finance payloads into shard bundles. Deterministic
 * given (core, txns): same inputs → same bundles → same hashes. The "hot"
 * year (transactions sharded per month) is the generatedAt year — when a new
 * year starts, the old year's months fold into one frozen year shard on the
 * next materialization and clients converge via the manifest diff. */
export function materializeFinanceShards(core: FinanceCore, txns: Txn[]): Map<string, unknown> {
  const bundles = new Map<string, unknown>();
  const hotYear = (core.meta.generatedAt || "").slice(0, 4) || "9999";

  // Accounts → small meta rows + per-year flattened balance snapshots.
  const metaRows: Omit<Account, "balances">[] = [];
  const balByYear = new Map<string, BalanceRowTuple[]>();
  for (const a of core.accounts) {
    const { balances, ...meta } = a;
    metaRows.push(meta);
    for (const [date, value] of Object.entries(balances)) {
      const y = date.slice(0, 4);
      let rows = balByYear.get(y);
      if (!rows) balByYear.set(y, (rows = []));
      rows.push([a.id, date, value]);
    }
  }
  bundles.set("accounts-meta", { accounts: metaRows } satisfies AccountsMetaBundle);
  for (const [y, rows] of [...balByYear].sort()) bundles.set(`balances:${y}`, { rows } satisfies BalancesBundle);

  // Net-worth series, per year.
  for (const [y, b] of chunkNetworthYears(core.networth)) bundles.set(`networth:${y}`, b);

  bundles.set("rollups", core.rollups);
  bundles.set("projection", core.projection);
  bundles.set("meta", core.meta);

  // Transactions: frozen years whole, hot (and any future-dated) year by month.
  // Rows keep their stored relative order within each shard. SIZE-AWARE
  // FOLDING (docs/firestore-migration.md FS9): a frozen year whose folded
  // shard would exceed TXN_YEAR_FOLD_LIMIT stays month-sharded instead —
  // Firestore caps docs at 1 MiB, and the manifest drives the client, so a
  // month-sharded old year is schema-compatible (reassembly's key sort
  // already interleaves txn:YYYY and txn:YYYY-MM in correct date order).
  // Deterministic given (core, txns); no live year triggers it today
  // (largest frozen year ≈ 516 KB).
  const byYear = new Map<string, Txn[]>();
  for (const t of txns) {
    const y = t.date.slice(0, 4);
    let rows = byYear.get(y);
    if (!rows) byYear.set(y, (rows = []));
    rows.push(t);
  }
  const txnShards = new Map<string, Txn[]>();
  for (const [y, rows] of byYear) {
    if (y >= hotYear || JSON.stringify(rows).length > TXN_YEAR_FOLD_LIMIT) {
      for (const t of rows) {
        const key = `txn:${t.date.slice(0, 7)}`;
        let m = txnShards.get(key);
        if (!m) txnShards.set(key, (m = []));
        m.push(t);
      }
    } else {
      txnShards.set(`txn:${y}`, rows);
    }
  }
  for (const [k, rows] of [...txnShards].sort()) bundles.set(k, rows);

  return bundles;
}

/** A frozen-year txn shard bigger than this stays month-sharded (see above). */
export const TXN_YEAR_FOLD_LIMIT = 700_000;

/** The per-year slicing of the net-worth series — shared by the shard
 * materializer (networth:<year> bundles) and the Firestore derived docs
 * (finance_derived/networth-<year>, docs/firestore-migration.md FS8), so
 * the two can't drift. Assembly is assembleNetworth / reassembleFinance:
 * years ascending, per-slice order preserved — byte-identical round-trip
 * (guarded by test-finance-shards + test-data-repos). */
export function chunkNetworthYears(networth: NetWorthData): [string, NetworthBundle][] {
  const nwYears = new Map<string, NetworthBundle>();
  for (const m of networth.months) {
    const y = m.slice(0, 4);
    let b = nwYears.get(y);
    if (!b) nwYears.set(y, (b = { months: [], series: {} }));
    b.months.push(m);
  }
  for (const [name, points] of Object.entries(networth.series)) {
    for (const [date, v] of Object.entries(points)) {
      const y = date.slice(0, 4);
      let b = nwYears.get(y);
      if (!b) nwYears.set(y, (b = { months: [], series: {} }));
      (b.series[name] ??= {})[date] = v;
    }
  }
  return [...nwYears].sort();
}

/** Inverse of chunkNetworthYears. `months` reproduces the stored array
 * byte-for-byte (years concat ascending, per-year order preserved). Series
 * maps come back in CANONICAL date-descending order — the monolith's maps
 * were newest-first except where mergeNetWorth's splices appended touched
 * months at the historic insertion point, an order no deterministic
 * chunking can reproduce and no consumer reads (charts/asof index by key).
 * Round-trip is therefore semantic, not byte-level, on the series maps. */
export function assembleNetworth(years: Iterable<[string, NetworthBundle]>): NetWorthData {
  const sorted = [...years].sort((a, c) => (a[0] < c[0] ? -1 : 1));
  const months: string[] = [];
  for (const [, b] of sorted) months.push(...b.months);
  const series: Record<string, Record<string, number>> = {};
  for (const [, b] of [...sorted].reverse()) {
    for (const [name, pts] of Object.entries(b.series)) Object.assign((series[name] ??= {}), pts);
  }
  return { months, series };
}

/** Rebuild the exact client payloads from shard bundles. Returns null when a
 * required bundle is missing (caller decides how to heal — usually by
 * fetching whatever the manifest lists that the cache lacks).
 *
 * Transactions: the stored array is globally date-desc, so period shards are
 * contiguous slices of it — concatenating shards newest-period-first with
 * per-shard order preserved reproduces the original array exactly. */
export function reassembleFinance(bundles: ReadonlyMap<string, unknown>): { core: FinanceCore; txns: Txn[] } | null {
  const am = bundles.get("accounts-meta") as AccountsMetaBundle | undefined;
  const meta = bundles.get("meta") as Meta | undefined;
  const rollups = bundles.get("rollups") as Rollups | undefined;
  const projection = bundles.get("projection") as ProjectionData | undefined;
  if (!am || !meta || !rollups || !projection) return null;

  const balancesById = new Map<string, Record<string, number>>();
  for (const k of [...bundles.keys()].filter((k) => k.startsWith("balances:")).sort()) {
    for (const [id, date, v] of (bundles.get(k) as BalancesBundle).rows) {
      let m = balancesById.get(id);
      if (!m) balancesById.set(id, (m = {}));
      m[date] = v;
    }
  }
  const accounts: Account[] = am.accounts.map((a) => ({ ...a, balances: balancesById.get(a.id) ?? {} }));

  const networth = assembleNetworth(
    [...bundles.keys()]
      .filter((k) => k.startsWith("networth:"))
      .map((k) => [k.slice("networth:".length), bundles.get(k) as NetworthBundle]),
  );

  const txns: Txn[] = [];
  const txnKeys = [...bundles.keys()].filter((k) => k.startsWith("txn:")).sort().reverse();
  for (const k of txnKeys) txns.push(...(bundles.get(k) as Txn[]));

  return { core: { accounts, networth, rollups, projection, meta }, txns };
}
